import React, { useEffect, useState, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Download, Share2, Home, CheckCircle, FileText } from 'lucide-react';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CertificatePage = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const certificateRef = useRef(null);
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchCertificate();
  }, [certificateId]);

  const fetchCertificate = async () => {
    try {
      // Get student profile for photo
      let studentProfile = null;
      try {
        const profileRes = await axios.get(`${API}/profile/student`);
        studentProfile = profileRes.data;
      } catch (e) {}

      // Try to get certificate from results
      const response = await axios.get(`${API}/assessment/my-results`);
      const result = response.data.find(r => r.certificate_id === certificateId);
      
      if (result) {
        const coursesRes = await axios.get(`${API}/assessment/courses`);
        const course = coursesRes.data.find(c => c.id === result.course_id);
        
        // Get enrollment to determine if RPL or STT
        const enrollmentsRes = await axios.get(`${API}/assessment/my-enrollments`);
        const enrollment = enrollmentsRes.data.find(e => e.course_id === result.course_id);
        const isRPL = enrollment?.enrollment_type === 'RPL';
        
        const now = new Date();
        // Certificate number format: CAN-XXXXXX
        const certNum = `CAN-${certificateId.substring(0, 6).toUpperCase()}`;
        
        const dobFormatted = studentProfile?.dob 
          ? new Date(studentProfile.dob).toLocaleDateString('en-IN', {
              day: '2-digit', month: '2-digit', year: 'numeric'
            }).replace(/\//g, '/')
          : user?.dob || '01/01/2000';
        
        setCertificate({
          id: certificateId,
          candidateName: user?.name || 'Student',
          fatherName: studentProfile?.father_name || user?.father_name || 'Guardian',
          dob: dobFormatted,
          photoUrl: studentProfile?.photo_url || '',
          courseName: course?.name || result.course_name || 'Assessment Course',
          // RPL = 40 hrs, STT = actual duration
          duration: isRPL ? '40 Hrs' : (course?.duration || '180 Hrs'),
          isRPL: isRPL,
          issueDate: now.toLocaleDateString('en-IN', { 
            day: '2-digit', month: '2-digit', year: 'numeric' 
          }).replace(/\//g, '.'),
          // Certificate Number = Enrollment Number (same value), format CAN-XXXXXX
          certificateNumber: certNum,
          enrollmentNumber: certNum,
          score: result.percentage,
          grade: result.percentage >= 80 ? 'A' : result.percentage >= 60 ? 'B' : 'C',
          trainingCenter: 'IQRA SKILL CENTRE',
          district: studentProfile?.district || 'Churu',
          state: studentProfile?.state || 'RAJASTHAN',
          creditsLevel: '4',
          verificationCode: certificateId
        });
      } else {
        toast.error('Certificate not found');
        navigate('/assessment');
      }
    } catch (error) {
      toast.error('Failed to load certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPNG = async () => {
    if (!certificateRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 3,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true
      });
      
      const link = document.createElement('a');
      link.download = `Certificate_${certificate.certificateNumber}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Certificate downloaded as PNG!');
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!certificateRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 3,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Certificate_${certificate.certificateNumber}.pdf`);
      toast.success('Certificate downloaded as PDF!');
    } catch (error) {
      toast.error('PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/certificate-verification?code=${certificate.verificationCode}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Iqra Skills Certificate',
          text: `I earned a certificate in ${certificate.courseName} from Iqra Skills!`,
          url: shareUrl
        });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Verification link copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!certificate) return null;

  const verificationUrl = `${window.location.origin}/certificate-verification?code=${certificate.verificationCode}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50 py-6 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Action Buttons */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/assessment')}>
            <Home className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleShare}>
              <Share2 className="h-4 w-4 mr-2" /> Share
            </Button>
            <Button variant="outline" onClick={handleDownloadPNG} disabled={downloading}>
              <Download className="h-4 w-4 mr-2" /> PNG
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleDownloadPDF} disabled={downloading}>
              <FileText className="h-4 w-4 mr-2" /> {downloading ? 'Downloading...' : 'Download PDF'}
            </Button>
          </div>
        </div>

        {/* Certificate - Updated Skill India Format */}
        <div 
          ref={certificateRef}
          className="bg-white shadow-2xl mx-auto relative overflow-hidden"
          style={{ width: '900px', height: '636px' }}
        >
          {/* Watermark - Iqra Skills Logo */}
          <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
            <div className="text-[200px] font-bold text-blue-900" style={{ fontFamily: 'Georgia, serif' }}>
              IQRA
            </div>
          </div>

          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-3" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Ctext x='5' y='25' font-size='8' fill='%230066cc' opacity='0.1'%3ESKILL%3C/text%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>

          {/* Border */}
          <div className="absolute inset-2 border-2 border-blue-500"></div>
          <div className="absolute inset-3 border border-emerald-400"></div>

          {/* Content */}
          <div className="relative z-10 p-5 h-full flex flex-col">
            {/* Header with Logos - IQRA left, Skill India middle, Photo right */}
            <div className="flex justify-between items-start mb-3">
              {/* IQRA SKILLS Logo - Larger */}
              <div className="text-center w-28">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-orange-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                  <div className="text-white text-center">
                    <span className="text-2xl font-bold block">IQRA</span>
                    <span className="text-[10px]">SKILL</span>
                  </div>
                </div>
                <p className="text-xs font-bold text-gray-800 mt-1">IQRA SKILLS PVT LTD</p>
                <p className="text-[8px] text-gray-500">A WARRIOR OF SKILLS</p>
              </div>

              {/* Center - Skill India Logo & Title */}
              <div className="flex-1 text-center px-2">
                {/* Skill India Logo - Larger */}
                <div className="w-20 h-20 mx-auto mb-2 rounded-full overflow-hidden border-3 border-orange-400 shadow-lg flex items-center justify-center" style={{
                  background: 'linear-gradient(180deg, #FF9933 0%, #FF9933 33%, #FFFFFF 33%, #FFFFFF 66%, #138808 66%, #138808 100%)'
                }}>
                  <div className="bg-white/90 rounded-full w-14 h-14 flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-900 text-center leading-tight">SKILL<br/>INDIA</span>
                  </div>
                </div>
                <p className="text-base font-bold text-blue-800">कौशल योग्यता प्रमाणपत्र</p>
                <h1 className="text-xl font-bold text-blue-900" style={{ fontFamily: 'Georgia, serif' }}>
                  Certificate for Skill Competency
                </h1>
              </div>

              {/* Candidate Photo - Right Side */}
              <div className="text-center w-28">
                <div className="w-24 h-28 mx-auto border-2 border-blue-400 bg-slate-100 overflow-hidden flex items-center justify-center">
                  {certificate.photoUrl ? (
                    <img src={certificate.photoUrl} alt="Candidate" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-slate-400 text-xs text-center p-2">
                      <div className="text-3xl mb-1">👤</div>
                      Candidate Photo
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-gray-600 mt-1">Certificate No:</p>
                <p className="text-xs font-bold text-gray-800">{certificate.certificateNumber}</p>
              </div>
            </div>

            {/* Certification Text */}
            <div className="text-center mb-2">
              <p className="text-sm text-gray-600">
                प्रमाणित किया जाता है कि श्री/सुश्री | This is to certify that Mr./Ms.
              </p>
              <h2 className="text-2xl font-bold text-blue-900 border-b-2 border-blue-300 inline-block px-6 py-1" 
                  style={{ fontFamily: 'Georgia, serif' }}>
                {certificate.candidateName}
              </h2>
            </div>

            {/* Main Content - Two Columns */}
            <div className="grid grid-cols-2 gap-3 flex-1">
              {/* Left Column */}
              <div className="space-y-1 text-sm">
                <div className="flex gap-2">
                  <span className="text-gray-600 w-28">पुत्र/पुत्री / S/o:</span>
                  <span className="font-semibold text-gray-800">{certificate.fatherName}</span>
                </div>
                
                <div className="pt-1 border-t border-gray-200">
                  <p className="text-gray-600 text-xs">
                    ने जॉब रोल/अहर्ता का आंकलन सफलतापूर्वक उत्तीर्ण किया है | has successfully cleared the assessment:
                  </p>
                  <p className="font-bold text-blue-800 text-lg mt-1">{certificate.courseName}</p>
                </div>
                
                <div className="flex gap-2">
                  <span className="text-gray-600 w-28">अवधि / Duration:</span>
                  <span className="font-bold text-emerald-700">{certificate.duration}</span>
                </div>
                
                <div className="flex gap-2">
                  <span className="text-gray-600 w-28">प्रशिक्षण केन्द्र:</span>
                  <span className="font-semibold">{certificate.trainingCenter}</span>
                </div>
                
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-gray-600">श्रेणी / Grade:</span>
                  <span className="bg-emerald-100 text-emerald-700 font-bold text-lg px-3 py-0.5 rounded">
                    {certificate.grade} ({certificate.score?.toFixed(0)}%)
                  </span>
                </div>
              </div>
              
              {/* Right Column */}
              <div className="space-y-1 text-sm">
                <div className="flex gap-2">
                  <span className="text-gray-600 w-32">जन्म तिथि / DOB:</span>
                  <span className="font-semibold">{certificate.dob}</span>
                </div>
                
                <div className="flex gap-2">
                  <span className="text-gray-600 w-32">नामांकन संख्या:</span>
                  <span className="font-semibold text-xs">{certificate.enrollmentNumber}</span>
                </div>
                
                <div className="flex gap-2">
                  <span className="text-gray-600 w-32">Credits/NSQF Level:</span>
                  <span className="font-bold text-blue-700">{certificate.creditsLevel}</span>
                </div>
                
                {/* District, State, and Qualification are hidden as per user requirement */}
              </div>
            </div>

            {/* Skill India Motto */}
            <div className="text-center my-1">
              <p className="text-lg font-bold text-blue-800" style={{ fontFamily: 'Georgia, serif' }}>
                कौशल भारत - कुशल भारत
              </p>
            </div>

            {/* Footer - QR, Issue Details, Seal */}
            <div className="flex justify-between items-end pt-2 border-t border-gray-200">
              {/* QR Code */}
              <div className="text-center">
                <div className="bg-white p-1 border border-gray-300 rounded inline-block">
                  <QRCodeSVG 
                    value={verificationUrl}
                    size={70}
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <p className="text-[9px] text-gray-500 mt-1">Scan to Verify</p>
              </div>
              
              {/* Issue Details */}
              <div className="text-center text-sm">
                <div className="mb-1">
                  <p className="text-gray-600 text-xs">जारी करने का स्थान / Place</p>
                  <p className="font-semibold">{certificate.state}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs">जारी करने की तिथि / Date</p>
                  <p className="font-semibold">{certificate.issueDate}</p>
                </div>
              </div>
              
              {/* Seal and Signature */}
              <div className="text-center">
                <div className="relative w-28 h-20">
                  <img 
                    src="/assets/seal.png" 
                    alt="Seal" 
                    className="absolute inset-0 w-full h-full object-contain opacity-80"
                    style={{ mixBlendMode: 'multiply' }}
                  />
                  <img 
                    src="/assets/signature.jpg" 
                    alt="Signature" 
                    className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-16 h-8 object-contain"
                    style={{ mixBlendMode: 'multiply' }}
                  />
                </div>
                <div className="border-t border-gray-400 pt-0.5 w-24 mx-auto">
                  <p className="text-xs font-bold">Director</p>
                  <p className="text-[9px] text-gray-500">IQRA SKILLS PVT LTD</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Verification Info */}
        <div className="mt-4 bg-white rounded-lg p-4 flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-emerald-600" />
            <div>
              <p className="font-medium text-slate-900">Verified Certificate</p>
              <p className="text-sm text-slate-500">Certificate No: {certificate.certificateNumber} | Scan QR or verify online</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(`/certificate-verification?code=${certificate.verificationCode}`)}>
            Verify Online
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CertificatePage;
